import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowRight, TrendingUp, Shield, FileCheck, Zap, Building, DollarSign, ClipboardCheck, Leaf } from 'lucide-react';

const Services = () => {
  const services = [
    {
      icon: <TrendingUp size={40} />,
      title: 'Feasibility & Financial Appraisal',
      shortDesc: 'Data-driven project viability assessments from concept to detailed analysis',
      benefits: [
        'Early-stage financial modeling and sensitivity analysis',
        'Site evaluation and development option appraisals',
        'Market analysis and demand assessment',
        'Risk identification and mitigation planning',
        'Investment decision support with clear, actionable insights',
      ],
      image: 'https://images.unsplash.com/photo-1615406020658-6c4b805f1f30',
    },
    {
      icon: <DollarSign size={40} />,
      title: 'Cost & Risk Management',
      shortDesc: 'Pragmatic cost control and risk mitigation throughout project delivery',
      benefits: [
        'Cost planning and budget development from concept stage',
        'Life-cycle costing and value engineering',
        'Cost monitoring and variance reporting',
        'Change management and claims support',
        'Risk registers and proactive mitigation strategies',
      ],
      image: 'https://images.pexels.com/photos/18435276/pexels-photo-18435276.jpeg',
    },
    {
      icon: <ClipboardCheck size={40} />,
      title: 'Procurement Advisory',
      shortDesc: 'Independent procurement support for optimal contractor and consultant selection',
      benefits: [
        'Procurement strategy development and route selection',
        'Tender documentation preparation and review',
        'Contractor and consultant pre-qualification',
        'Tender evaluation and recommendation',
        'Contract negotiation support',
      ],
      image: 'https://images.pexels.com/photos/34840277/pexels-photo-34840277.jpeg',
    },
    {
      icon: <Leaf size={40} />,
      title: 'Building Energy Performance Services',
      shortDesc: 'Energy engineering expertise delivering BER assessments, performance optimization, and compliance solutions',
      benefits: [
        'Building Energy Rating (BER) assessments and certification by qualified energy engineers',
        'Energy modeling and performance analysis using industry-standard tools',
        'Design-stage energy engineering to achieve NZEB and Part L compliance',
        'Retrofit advisory combining engineering analysis with cost-benefit evaluation',
        'Whole-building energy optimization from fabric to systems',
        'SBEM/DEAP compliance modeling and reporting',
        'Sustainability reporting and ESG alignment for asset portfolios',
      ],
      image: 'https://images.pexels.com/photos/424172/pexels-photo-424172.jpeg',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-[#0d1b2e] text-white py-20">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-br from-[#4a9ece] to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 animate-fadeIn">
              Our Services
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              Comprehensive engineering-led advisory services to support your project from concept through to exit. Independent expertise you can trust at every stage.
            </p>
          </div>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {services.map((service, index) => (
              <div
                key={index}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[#4a9ece] text-white rounded-lg mb-6">
                    {service.icon}
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold text-[#0d1b2e] mb-4">
                    {service.title}
                  </h2>
                  <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                    {service.shortDesc}
                  </p>
                  <div className="space-y-3">
                    <h3 className="text-xl font-semibold text-[#0d1b2e] mb-4">Key Benefits:</h3>
                    <ul className="space-y-3">
                      {service.benefits.map((benefit, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <div className="w-1.5 h-1.5 bg-[#4a9ece] rounded-full mt-2.5 flex-shrink-0" />
                          <span className="text-slate-700">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <img
                    src={service.image}
                    alt={service.title}
                    className="rounded-lg shadow-xl w-full h-[400px] object-cover"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0d1b2e] mb-4">
              How We Deliver Value
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Our services are designed to integrate seamlessly across the project lifecycle
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <Card className="border-slate-200 hover:border-[#4a9ece] transition-all duration-300">
              <CardHeader>
                <div className="w-12 h-12 bg-[#4a9ece] text-white rounded-full flex items-center justify-center mb-4 text-xl font-bold">
                  1
                </div>
                <CardTitle className="text-[#0d1b2e]">Concept Stage</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600">
                  Feasibility studies and financial modeling to establish project viability before commitment.
                </p>
              </CardContent>
            </Card>

            <Card className="border-slate-200 hover:border-[#4a9ece] transition-all duration-300">
              <CardHeader>
                <div className="w-12 h-12 bg-[#4a9ece] text-white rounded-full flex items-center justify-center mb-4 text-xl font-bold">
                  2
                </div>
                <CardTitle className="text-[#0d1b2e]">Planning & Design</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600">
                  Cost planning, value engineering, and energy performance optimization during design development.
                </p>
              </CardContent>
            </Card>

            <Card className="border-slate-200 hover:border-[#4a9ece] transition-all duration-300">
              <CardHeader>
                <div className="w-12 h-12 bg-[#4a9ece] text-white rounded-full flex items-center justify-center mb-4 text-xl font-bold">
                  3
                </div>
                <CardTitle className="text-[#0d1b2e]">Procurement</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600">
                  Independent procurement advisory to secure the right team and commercial terms.
                </p>
              </CardContent>
            </Card>

            <Card className="border-slate-200 hover:border-[#4a9ece] transition-all duration-300">
              <CardHeader>
                <div className="w-12 h-12 bg-[#4a9ece] text-white rounded-full flex items-center justify-center mb-4 text-xl font-bold">
                  4
                </div>
                <CardTitle className="text-[#0d1b2e]">Delivery & Exit</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600">
                  Cost and risk management through construction, plus BER certification to maximize asset value at exit.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#0d1b2e] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            Contact us to discuss how our services can support your next development project.
          </p>
          <Link to="/contact">
            <Button className="bg-[#4a9ece] hover:bg-[#3a8ebe] text-white px-10 py-6 text-lg">
              Contact Us Today
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
